<template>
    <div id="pricing" class="bg-gray-900">
        <div class="px-4 pt-12 sm:px-6 lg:px-8 lg:pt-20">
            <div class="text-center">
                <p class="text-3xl font-extrabold leading-9 text-white mt- sm:text-4xl sm:leading-10 lg:text-5xl lg:leading-none">Pricing Plans</p>
                <p class="max-w-4xl mx-auto mt-3 text-xl leading-7 text-gray-300 sm:mt-5 sm:text-2xl sm:leading-8">Choose the best plan for your team.</p>
                <div class="grid items-center w-full grid-cols-3 gap-3 mx-auto mt-4 lg:w-64">
                    <div class="mx-auto font-medium leading-7" :class="[!yearly ? 'text-indigo-500' : 'text-gray-400']">Monthly</div>
                    <div
                        class="relative inline-flex items-center justify-center w-10 h-5 mx-auto cursor-pointer flex-no-shrink focus:outline-none"
                        role="checkbox"
                        tabindex="0"
                        @click="toggle()"
                        @keydown.space.prevent="toggle()"
                        :aria-checked="yearly.toString()"
                    >
                        <span
                            aria-hidden="true"
                            :class="{ 'bg-indigo-600': yearly, 'bg-gray-200': !yearly }"
                            class="absolute h-4 mx-auto transition-colors duration-200 ease-in-out rounded-full w-9"
                        ></span>
                        <span
                            aria-hidden="true"
                            :class="{ 'translate-x-5': yearly, 'translate-x-0': !yearly, 'shadow-outline border-blue-300': focused }"
                            class="absolute left-0 inline-block w-5 h-5 transition-transform duration-200 ease-in-out transform bg-white border border-gray-200 rounded-full shadow"
                        ></span>
                    </div>
                    <div class="mx-auto -ml-4 font-medium leading-7 lg:w-64" :class="[yearly ? 'text-indigo-500' : 'text-gray-400']">
                        Yearly <span class="ml-1 text-gray-500">Get 2 months free</span>
                    </div>
                </div>
            </div>
        </div>

        <div class="pb-12 mt-16 bg-white lg:mt-20 lg:pb-20">
            <div class="relative z-0">
                <div class="absolute inset-0 bg-gray-900 h-5/6 lg:h-2/3"></div>
                <div class="max-w-screen-xl px-4 mx-auto sm:px-6 lg:px-8">
                    <div class="relative lg:grid lg:grid-cols-7">
                        <div class="max-w-md mx-auto lg:mx-0 lg:max-w-none lg:col-start-1 lg:col-end-3 lg:row-start-2 lg:row-end-3">
                            <div class="flex flex-col h-full overflow-hidden rounded-lg shadow-lg lg:rounded-none lg:rounded-l-lg">
                                <div class="flex flex-col flex-1">
                                    <div class="px-6 py-10 bg-white">
                                        <div>
                                            <h2 class="text-2xl font-medium leading-8 text-center text-gray-900">Basic</h2>
                                            <p class="mx-8 text-base leading-5 text-center text-gray-400">Perfect for small teams looking to build strong teams</p>
                                            <div class="flex items-center justify-center mt-6">
                                                <span class="flex items-start px-3 text-6xl leading-none tracking-tight text-gray-900">
                                                    <span class="mt-2 mr-2 text-4xl font-medium">
                                                        $
                                                    </span>
                                                    <span v-if="yearly" class="font-extrabold">50</span>
                                                    <span v-else class="font-extrabold">5</span>
                                                </span>
                                                <span v-if="yearly" class="text-xl font-medium leading-7 text-gray-400">
                                                    /yearly
                                                </span>
                                                <span v-else class="text-xl font-medium leading-7 text-gray-400">
                                                    /month
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="flex flex-col justify-between flex-1 p-6 bg-white border-t-2 border-gray-100 sm:p-10 lg:p-6 xl:p-10">
                                        <ul>
                                            <li class="flex items-start">
                                                <div class="flex-shrink-0">
                                                    <svg class="w-6 h-6 text-green-500" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                                    </svg>
                                                </div>
                                                <p class="ml-3 text-base font-medium leading-6 text-gray-500">Single team</p>
                                            </li>
                                            <li class="flex items-start mt-4">
                                                <div class="flex-shrink-0">
                                                    <svg class="w-6 h-6 text-green-500" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                                    </svg>
                                                </div>
                                                <p class="ml-3 text-base font-medium leading-6 text-gray-500">Up to 3 members</p>
                                            </li>
                                            <li class="flex items-start mt-4">
                                                <div class="flex-shrink-0">
                                                    <svg class="w-6 h-6 text-green-500" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                                    </svg>
                                                </div>
                                                <p class="ml-3 text-base font-medium leading-6 text-gray-500">(soon) Basic Slack integration</p>
                                            </li>
                                        </ul>
                                        <div class="mt-8">
                                            <p class="mx-8 mb-2 text-sm leading-5 text-center text-gray-400">14 days free trial</p>
                                            <div class="rounded-lg shadow-md">
                                                <a
                                                    href="https://beta.flowist.io/signup?plan=basic"
                                                    class="block w-full py-2 mt-6 text-sm font-semibold leading-6 text-center text-white transition duration-150 ease-in-out bg-gray-900 border border-transparent rounded-md shadow hover:bg-primary-500 focus:outline-none focus:shadow-outline-gray"
                                                    >Get started</a
                                                >
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="max-w-lg mx-auto mt-10 lg:mt-0 lg:max-w-none lg:mx-0 lg:col-start-3 lg:col-end-6 lg:row-start-1 lg:row-end-4">
                            <div class="relative z-10 rounded-lg shadow-xl">
                                <div class="absolute inset-0 border-2 border-indigo-600 rounded-lg pointer-events-none"></div>
                                <div class="absolute inset-x-0 top-0 transform translate-y-px">
                                    <div class="flex justify-center transform -translate-y-1/2">
                                        <span class="inline-flex px-4 py-1 text-sm font-semibold leading-5 tracking-wider text-white uppercase bg-indigo-600 rounded-full"
                                            >Best Value</span
                                        >
                                    </div>
                                </div>
                                <div class="px-6 pt-12 pb-10 bg-white rounded-t-lg">
                                    <div>
                                        <h2 class="text-3xl font-semibold leading-9 text-center text-gray-900 sm:-mx-6">Pro</h2>
                                        <p class="mx-16 text-base leading-5 text-center text-gray-400">For startups and businesses working on the next big thing</p>
                                        <div class="flex items-center justify-center mt-6">
                                            <span class="flex items-start px-3 text-6xl leading-none tracking-tight text-gray-900 sm:text-6xl">
                                                <span class="mt-2 mr-2 text-4xl font-medium">
                                                    $
                                                </span>
                                                <span v-if="yearly" class="font-extrabold">150</span>
                                                <span v-else class="font-extrabold">15</span>
                                            </span>
                                            <span v-if="yearly" class="text-xl font-medium leading-7 text-gray-400">
                                                /yearly
                                            </span>
                                            <span v-else class="text-xl font-medium leading-7 text-gray-400">
                                                /month
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div class="px-6 pt-10 pb-8 bg-white border-t-2 border-gray-100 rounded-b-lg sm:px-10 sm:py-10">
                                    <ul>
                                        <li class="flex items-start">
                                            <div class="flex-shrink-0">
                                                <svg class="w-6 h-6 text-green-500" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                            <p class="ml-3 text-base font-medium leading-6 text-gray-500">Up to 5 teams</p>
                                        </li>
                                        <li class="flex items-start mt-4">
                                            <div class="flex-shrink-0">
                                                <svg class="w-6 h-6 text-green-500" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                            <p class="ml-3 text-base font-medium leading-6 text-gray-500">Up to 20 members</p>
                                        </li>
                                        <li class="flex items-start mt-4">
                                            <div class="flex-shrink-0">
                                                <svg class="w-6 h-6 text-green-500" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                            <p class="ml-3 text-base font-medium leading-6 text-gray-500">(soon) Full Slack integration</p>
                                        </li>
                                    </ul>
                                    <div class="mt-10">
                                        <p class="mx-8 mb-2 text-sm leading-5 text-center text-gray-400">14 days free trial</p>
                                        <div class="rounded-lg shadow-md">
                                            <a
                                                href="https://beta.flowist.io/signup?plan=pro"
                                                class="block w-full px-6 py-4 text-xl font-medium leading-6 text-center text-white transition duration-150 ease-in-out border border-transparent rounded-md shadow bg-gradient-to-r from-primary-500 to-purple-500 hover:from-primary-700 focus:outline-none focus:shadow-outline-gray"
                                                >Start 14-day trial</a
                                            >
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="max-w-md mx-auto mt-10 lg:m-0 lg:max-w-none lg:col-start-6 lg:col-end-8 lg:row-start-2 lg:row-end-3">
                            <div class="flex flex-col h-full overflow-hidden rounded-lg shadow-lg lg:rounded-none lg:rounded-r-lg">
                                <div class="flex flex-col flex-1">
                                    <div class="px-6 py-10 bg-white">
                                        <div>
                                            <h2 class="text-2xl font-medium leading-8 text-center text-gray-900">Business</h2>
                                            <p class="mx-8 text-base leading-5 text-center text-gray-400">For the enterprise looking to become more connected</p>
                                            <div class="flex items-center justify-center mt-6">
                                                <span class="flex items-start px-3 text-6xl leading-none tracking-tight text-gray-900">
                                                    <span class="mt-2 mr-2 text-4xl font-medium">
                                                        $
                                                    </span>
                                                    <span v-if="yearly" class="font-extrabold">350</span>
                                                    <span v-else class="font-extrabold">35</span>
                                                </span>
                                                <span v-if="yearly" class="text-xl font-medium leading-7 text-gray-400">
                                                    /yearly
                                                </span>
                                                <span v-else class="text-xl font-medium leading-7 text-gray-400">
                                                    /month
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="flex flex-col justify-between flex-1 p-6 bg-white border-t-2 border-gray-100 sm:p-10 lg:p-6 xl:p-10">
                                        <ul>
                                            <li class="flex items-start">
                                                <div class="flex-shrink-0">
                                                    <svg class="w-6 h-6 text-green-500" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                                    </svg>
                                                </div>
                                                <p class="ml-3 text-base font-medium leading-6 text-gray-500">Up to 10 teams</p>
                                            </li>
                                            <li class="flex items-start mt-4">
                                                <div class="flex-shrink-0">
                                                    <svg class="w-6 h-6 text-green-500" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                                    </svg>
                                                </div>
                                                <p class="ml-3 text-base font-medium leading-6 text-gray-500">Up to 50 members</p>
                                            </li>
                                            <li class="flex items-start mt-4">
                                                <div class="flex-shrink-0">
                                                    <svg class="w-6 h-6 text-green-500" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                                    </svg>
                                                </div>
                                                <p class="ml-3 text-base font-medium leading-6 text-gray-500">(soon) Full Slack integration</p>
                                            </li>
                                        </ul>
                                        <div class="mt-8">
                                            <p class="mx-8 mb-2 text-sm leading-5 text-center text-gray-400">14 days free trial</p>
                                            <div class="rounded-lg shadow-md">
                                                <a
                                                    href="https://beta.flowist.io/signup?plan=business"
                                                    class="block w-full py-2 mt-6 text-sm font-semibold leading-6 text-center text-white transition duration-150 ease-in-out bg-gray-900 border border-transparent rounded-md shadow hover:bg-primary-500 focus:outline-none focus:shadow-outline-gray"
                                                    >Get started</a
                                                >
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="container relative px-4 mx-auto mt-4 sm:px-6 lg:px-8 lg:mt-5">
                <div>
                    <div class="px-6 py-8 bg-gray-100 bg-gray-900 rounded-lg sm:p-10 lg:flex lg:items-center">
                        <div class="flex-1">
                            <div>
                                <span class="inline-flex px-4 py-1 text-sm font-semibold leading-5 tracking-wide text-gray-800 uppercase bg-white rounded-full">ENTERPRISE</span>
                            </div>
                            <div class="mt-4 text-lg leading-7 text-gray-400">
                                No plan fits your need? We got you covered. Please contact us and we will build a plan that fits your needs!
                            </div>
                        </div>
                        <div class="mt-6 rounded-md shadow cursor-pointer lg:mt-0 lg:ml-10 lg:flex-shrink-0">
                            <a
                                href="mailto:jbaldwin@hey.com"
                                title="Choose Enterprise Plan"
                                class="flex items-center justify-center px-5 py-3 text-base font-medium leading-6 text-gray-900 transition duration-150 ease-in-out bg-white border border-transparent rounded-md hover:text-gray-700 focus:outline-none focus:shadow-outline"
                                >Contact Us</a
                            >
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
export default {
    name: 'app-tiers',
    data() {
        return {
            yearly: false,
        };
    },
    methods: {
        toggle() {
            this.yearly = !this.yearly;
        },
    },
};
</script>
<style></style>
